import React, { useState } from "react";
import styles from "../style";
// Images - replace with your actual assets
import {
  solution1,
  solution2,
  solution3,
  solution4,
} from "../assets";

const Solution = () => {
  const [expandedCard, setExpandedCard] = useState("digitize");

  // ---- DATA ---------------------------------------------------------------
  const solutions = [
    {
      id: "digitize",
      title: "Complete Street Inventory",
      subtitle: "From Video to Digital Twin",
        brief: "Transform dashcam footage into a comprehensive digital twin of your entire street infrastructure",
          description: "Our computer vision AI automatically detects, maps, and maintains a complete inventory of street assets from lane configurations to fire hydrants, crosswalks to parking signs.",
      capabilities: [
        {
          title: "Automated Asset Detection",
          detail: "AI identifies signs, meters, paint, bike lanes, crosswalks, and regulations from video"
        },
        {
          title: "Dimensional Analysis",
          detail: "Lane widths, crosswalk sizes, spacing measurements"
        },
        {
          title: "Regulation Extraction",
          detail: "Parking rules, time limits, restrictions from sign text"
        },
        {
          title: "Living Digital Twin",
          detail: "Continuous updates keep your curb data current and accurate"
        },
        {
          title: "CDS Compliance",
          detail: "Export-ready for Curb Data Specification standards"
        }
      ],
      metrics: [
        { value: "100%", label: "Detection Accuracy" },
        { value: "10x", label: "Faster than Manual" },
        { value: "Real-time", label: "Update Frequency" }
      ],
      techStack: ["Computer Vision", "Deep Learning", "GIS Integration"],
      image: solution1,
      gradient: "from-blue-600 to-cyan-500",
    },
    {
      id: "occupancy",
      title: "Occupancy Intelligence",
      subtitle: "Predictive Analytics & Real-time Monitoring",
      brief: "Monitor, analyze, and predict curb usage patterns to optimize availability",
      description: "Track real-time occupancy, understand demand patterns, and forecast availability to reduce cruising and improve curb utilization.",
      capabilities: [
        {
          title: "Live Occupancy Tracking",
          detail: "Real-time vehicle detection and space availability"
        },
        {
          title: "Demand Forecasting",
          detail: "ML models predict future occupancy patterns"
        },
        {
          title: "Duration Analytics",
          detail: "Track dwell times and turnover rates"
        },
        {
          title: "API Integration",
          detail: "Feed data to navigation, payment, and enforcement systems"
        }
      ],
      metrics: [
        { value: "30%", label: "Less Cruising" },
        { value: "24/7", label: "Monitoring" },
        { value: "<2min", label: "Data Latency" }
      ],
      techStack: ["Predictive ML", "Stream Processing", "REST APIs"],
      image: solution2,
      gradient: "from-purple-600 to-pink-500",
    },
    {
      id: "operations",
      title: "Operational Platform",
      subtitle: "Cloud Dashboard & Integration Hub",
      brief: "Unify all curb data and operations in one intelligent platform",
      description: "Centralized command center for curb management with seamless integrations to existing city systems and workflows.",
      capabilities: [
        {
          title: "Unified Dashboard",
          detail: "Single pane of glass for all curb operations"
        },
        {
          title: "Legacy Integration",
          detail: "Connect with existing parking, payment, and permit systems"
        },
        {
          title: "Workflow Automation",
          detail: "Trigger maintenance, enforcement, and policy actions"
        },
        {
          title: "Data Warehouse",
          detail: "Secure storage with role-based access control"
        }
      ],
      metrics: [
        { value: "100%", label: "System Coverage" },
        { value: "5-day", label: "Integration Time" },
        { value: "SOC2", label: "Compliance" }
      ],
      techStack: ["Cloud Native", "ETL Pipelines", "Enterprise Security"],
      image: solution3,
      gradient: "from-emerald-600 to-teal-500",
    },
    {
      id: "field",
      title: "Field Intelligence",
      subtitle: "Mobile Apps for Crews & Drivers",
      brief: "Empower field teams and drivers with real-time curb intelligence",
      description: "Purpose-built mobile applications that connect field operations to your digital twin for efficient maintenance and parking guidance.",
      capabilities: [
        {
          title: "Crew Dispatch",
          detail: "Route maintenance teams to flagged assets efficiently"
        },
        {
          title: "Field Verification",
          detail: "Validate and update curb data on-site"
        },
        {
          title: "Driver Navigation",
          detail: "Guide to available, legal parking spaces"
        },
        {
          title: "Offline Sync",
          detail: "Work seamlessly with or without connectivity"
        }
      ],
      metrics: [
        { value: "50%", label: "Faster Response" },
        { value: "iOS/Android", label: "Platform Support" },
      ],
      techStack: ["Offline-First", "GPS Navigation"],
      image: solution4,
      gradient: "from-orange-600 to-red-500",
    },
  ];

  const caseStudies = [
    {
      city: "Las Vegas",
      logo: "🎰",
      scope: "Arts District Pilot",
      // timeline: "3-week pilot",
      results: [
        "Curb and sign digitization of Arts District",
        "Automated data collection from dashcam footage",
        "User-friendly parking finder website launched",
        "Real-time parking availability from collected data",
        "Mobile application for street data maintenance"
      ],
      quote: "The digital twin transformed how we understand our streets.",
      metrics: { primary: "Live Platform", secondary: "Parking Finder" }
    },
    {
      city: "Bellevue, Washington",
      logo: "🏔️",
      scope: "Downtown Digitization",
      // timeline: "Active project",
      results: [
        "Downtown Bellevue street infrastructure captured",
        "Web-based inventory management platform",
        "Asset tracking and maintenance dashboard",
        "CDS Compliance"
      ],
      quote: "Finally, real-time visibility into our street network.",
      metrics: { primary: "Inventory Management", secondary: "Asset Management" }
    },
    {
      city: "San Francisco",
      logo: "🌉",
      scope: "LRS Development",
      // timeline: "Completed",
      results: [
        "Linear Referencing System implementation",
        "Street network topology mapping",
        "Foundation for digital curb integration"
      ],
      quote: "Critical infrastructure for modern street management.",
      metrics: { primary: "LRS", secondary: "Implemented" }
    }
  ];

  const [activeCase, setActiveCase] = useState(0);

  return (
    <section className={`${styles.flexCenter} flex-col ${styles.paddingY} bg-gradient-to-b from-white to-gray-50`}>
      <div className="w-full max-w-7xl px-4">
        
        {/* HERO SECTION - Digital Twin Focus */}
        <div className="relative mb-20 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 text-white shadow-2xl md:p-16">
          {/* Animated Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute h-96 w-96 -left-48 -top-48 rounded-full bg-blue-500 blur-3xl animate-pulse"></div>
            <div className="absolute h-96 w-96 -right-48 -bottom-48 rounded-full bg-emerald-500 blur-3xl animate-pulse delay-1000"></div>
          </div>
          
          <div className="relative z-10 max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>AI-Powered Curb Intelligence Platform</span>
            </div>
            
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
              Transform Dashcam Video into a
              <span className="block bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                Living Digital Twin
              </span>
            </h1>

            <p className="mb-4 text-xl font-medium text-blue-300">
              Street Infrastructure Digitization • Verification • System Integration
            </p>

            <p className="mb-8 max-w-2xl text-lg leading-relaxed text-gray-300">
              Step into the future of street infrastructure digitization. Our AI extracts lanes, 
              crosswalks, fire hydrants, signs, parking rules, and more from simple dashcam footage. 
              Build a comprehensive digital twin of your city's streets in weeks, not years.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => document.getElementById('case-studies').scrollIntoView({ behavior: 'smooth' })}
                className="group relative overflow-hidden rounded-full bg-white px-8 py-4 font-semibold text-slate-900 shadow-lg transition hover:shadow-xl hover:scale-105"
              >
                <span className="relative z-10">Case Studies</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-400 to-emerald-400 opacity-0 transition group-hover:opacity-10"></span>
              </button>
              <button 
                onClick={() => document.getElementById('solutions').scrollIntoView({ behavior: 'smooth' })}
                className="rounded-full border border-white/30 bg-white/10 backdrop-blur px-8 py-4 font-semibold transition hover:bg-white/20"
              >
                Explore Solutions ↓
              </button>
            </div>
            
            {/* Key Stats */}
            <div className="mt-12 grid grid-cols-3 gap-8 border-t border-white/20 pt-8">
              <div>
                <div className="text-3xl font-bold text-emerald-400">10x</div>
                <div className="text-sm text-gray-400">Faster Processing</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-400">150K+</div>
                <div className="text-sm text-gray-400">Street Infrastructure Assets Mapped</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-purple-400">CDS</div>
                <div className="text-sm text-gray-400">Compliant</div>
              </div>
            </div>
          </div>
        </div>

        {/* SOLUTIONS - All Expanded */}
        <div id="solutions" className="mb-20">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              Complete Street Intelligence Stack
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Four integrated solutions that work together to digitize, analyze, and optimize your entire street infrastructure ecosystem
            </p>
          </div>
          
          <div className="space-y-6">
{/* Updated solution card structure */}
{solutions.map((solution, index) => (
  <div
    key={solution.id}
    className="group relative overflow-hidden rounded-2xl bg-white shadow-lg transition-all hover:shadow-2xl"
  >
    {/* Gradient Border Effect */}
    <div className={`absolute inset-0 bg-gradient-to-r ${solution.gradient} opacity-5 group-hover:opacity-10 transition`}></div>
    
    <div className="relative p-8 md:p-10">
      <div className="grid gap-8 md:grid-cols-3">
        {/* Left: Overview */}
        <div className="md:col-span-1">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{solution.title}</h3>
              <p className="text-sm font-medium text-gray-500">{solution.subtitle}</p>
            </div>
            <span className={`inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${solution.gradient} text-white text-xl font-bold shadow-lg`}>
              {index + 1}
            </span>
          </div>
          
          <p className="mb-6 text-gray-700 leading-relaxed">{solution.brief}</p>
          
          <div className="mb-6 text-sm text-gray-600">{solution.description}</div>
          
          {/* Tech Stack Pills */}
          <div className="flex flex-wrap gap-2">
            {solution.techStack.map((tech, i) => (
              <span key={i} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                {tech}
              </span>
            ))}
          </div>
        </div>
        
        {/* Middle & Right: Capabilities spanning 2 columns */}
        <div className="md:col-span-2">
          <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Key Capabilities
          </h4>
          <div className="grid gap-3 md:grid-cols-2">
            {solution.capabilities.map((cap, i) => (
              <div key={i} className="rounded-lg bg-gray-50 p-3">
                <div className="mb-1 font-semibold text-gray-900">{cap.title}</div>
                <div className="text-sm text-gray-600">{cap.detail}</div>
              </div>
            ))}
          </div>
          
          {/* Single line metrics at bottom */}
          <div className="mt-6 flex flex-wrap items-center gap-4 border-t pt-4">
            {solution.metrics.map((metric, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className={`text-lg font-bold bg-gradient-to-r ${solution.gradient} bg-clip-text text-transparent`}>
                  {metric.value}
                </span>
                <span className="text-sm text-gray-600">{metric.label}</span>
                {i < solution.metrics.length - 1 && <span className="text-gray-300">•</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
))}
          </div>
        </div>

        {/* CASE STUDIES - Compact Grid */}
        <div id="case-studies" className="mb-20">

          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              Cities Leading the Way
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Join forward-thinking municipalities already transforming their curbs
            </p>
          </div>
          
          <div className="grid gap-6 md:grid-cols-3">
            {caseStudies.map((study, index) => (
              <div
                key={index}
                className="group cursor-pointer rounded-2xl bg-white p-8 shadow-lg transition-all hover:shadow-2xl hover:scale-105"
                onClick={() => setActiveCase(index)}
              >
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-4xl">{study.logo}</span>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                    {study.timeline}
                  </span>
                </div>
                
                <h3 className="mb-2 text-xl font-bold text-gray-900">{study.city}</h3>
                <p className="mb-4 text-sm text-gray-600">{study.scope}</p>
                
                
                <div className="mb-4 flex items-baseline gap-2 border-t pt-4">
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                    {study.metrics.primary}
                  </span>
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                    {study.metrics.secondary}
                  </span>
                </div>
                
                <ul className="mb-4 space-y-1">
                  {study.results.map((result, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500"></span>
                      {result}
                    </li>
                  ))}
                </ul>
                
                {/* <blockquote className="border-l-2 border-emerald-500 pl-3 text-sm italic text-gray-600">
                  "{study.quote}"
                </blockquote> */}
              </div>
            ))}
          </div>
        </div>

        {/* FINAL CTA */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 via-blue-600 to-purple-600 p-12 text-center text-white shadow-2xl">
          <div className="relative z-10">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Ready to Build Your Curb Digital Twin?
            </h2>
            <p className="mb-8 text-lg opacity-90">
              Transform your curb management in weeks, not years
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="#/contact"
                className="inline-block rounded-full bg-white px-8 py-4 font-bold text-gray-900 shadow-lg transition hover:scale-105 hover:shadow-xl"
              >
                Contact us to Schedule Demo
              </a>
            </div>
          </div>
        </div>
        
      </div>
    </section>
  );
};

export default Solution;